<script lang="ts" setup>
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { form, isLoaderLoginFormActivated } from '../../refs/login.form.refs';

// -------------------------------
// 🔹 Composants
// -------------------------------
import TextInput from '../common/BaseInput.vue';
import BaseButtonPrimary from '../common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Mutations / services
// -------------------------------
import { CommerceMutation } from '@/mutations/commerce.mutations';
import { FileMutation } from '@/mutations/file.mutation';
const { handleUpdateCommerce } = CommerceMutation();
const { handleUploadFile } = FileMutation();

// -------------------------------
// 🔹 Imports stores Pinia
// -------------------------------
import { useCommerceStore } from '@/store/commerce.store';
import { useDialogStore } from '@/store/dialog.store';

// -------------------------------
// 🔹 Imports assets / refs / données
// -------------------------------
import {
  idToDelete,
  isStepTwoActivated,
  previewUrl,
  file,
  form_CommerceBody,
  form_CommerceBodyUpdate,
  fileInput,
  errorMessage,
  isImageUploadid,
  isAtcivatedWatch,
  commerceId,
} from '@/refs/commerce.refs';
import {
  content,
  globalHeaderTitleLayout,
  isGlobalError,
  restaurantId,
} from '@/refs/common.refs';
import { IconUploadCloud } from '@/assets/svg/common.svg';
import type { IFileImage } from '@/types/file.types';
import type { ICommerceBodyUpdate } from '@/types/commerce.types';

// -------------------------------
// 🔹 Stores instanciés
// -------------------------------
const dialogStore = useDialogStore();

// -------------------------------
// 🔹 Émission d’événements
// -------------------------------
const emit = defineEmits<{
  (e: 'HandleClose'): void;
}>();

// -------------------------------
// 🔹 Gestion du formulaire
// -------------------------------

/**
 * Ferme le formulaire et réinitialise l’ID à supprimer.
 */
const handleClose = (): void => {
  if (isImageUploadid.value) {
    isImageUploadid.value = false;
    globalHeaderTitleLayout.value = 'Modification du commerce';
  } else {
    idToDelete.value = null;
    dialogStore.closeDialog();
  }
};

/**
 * Ouvre automatiquement le sélecteur de fichiers du navigateur.
 *
 * Cette fonction simule un clic sur l’input de type "file" caché,
 * afin de permettre à l’utilisateur de sélectionner une image sans
 * avoir à interagir directement avec le champ invisible.
 *
 * @function triggerUpload
 * @returns {void} Ne retourne aucune valeur
 */
const triggerUpload = () => {
  fileInput.value?.click();
};

/**
 * Gère la sélection d’un fichier et génère un aperçu visuel du logo.
 *
 * - Récupère le fichier sélectionné par l’utilisateur
 * - Vérifie sa taille maximale (5 Mo)
 * - Si valide, génère une URL temporaire pour l’afficher en aperçu
 * - Si invalide, affiche un message d’erreur
 *
 * @function handleFileChange
 * @param {Event} event - Événement déclenché lors du changement de fichier (input type="file")
 * @returns {void} Ne retourne aucune valeur
 */
const handleFileChange = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;

  // Validation taille max
  if (file.size > 5 * 1024 * 1024) {
    errorMessage.value = 'La taille maximale autorisée est de 5 Mo.';
    return;
  }

  // Génère l'aperçu

  try {
    const body: IFileImage = {
      designation: form_CommerceBodyUpdate.designation,
      file: file,
      restaurantId: commerceId.value,
    };
    const res = handleUploadFile(body);

    if (res) {
      isLoaderLoginFormActivated.value = true;
      previewUrl.value = URL.createObjectURL(file);

      setTimeout(() => {
        isLoaderLoginFormActivated.value = false;
      }, 1500);
      errorMessage.value = null;
    }
  } catch (error) {
    errorMessage.value =
      "Désolé, l'importation de cette image a échoué : " + error;

    return;
  }
};

/**
 * Supprime le logo sélectionné ainsi que son aperçu visuel.
 *
 * - Réinitialise l’URL d’aperçu
 * - Vide le champ de fichier
 * - Permet à l’utilisateur de choisir à nouveau une image
 *
 * @function handleDelete
 * @returns {void} Ne retourne aucune valeur
 */
const handleDelete = () => {
  previewUrl.value = null;

  // Réinitialise le champ fichier
  if (fileInput.value) fileInput.value.value = '';
};

/**
 * Soumet le formulaire de création de commerce.
 * Gère les différentes étapes : création du commerce, ajout du contact, ou upload du logo.
 */

const handleUplaodImageIfExist = async () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const body: IFileImage = {
      restaurantId: commerceId.value,
      file: file.value,
      designation: form_CommerceBodyUpdate.designation,
    };
    const res = await handleUploadFile(body);

    if (res) {
      const bodyCom: ICommerceBodyUpdate = {
        designation: form_CommerceBodyUpdate.designation,
        // orderCode: form_CommerceBodyUpdate.orderCode,
        // currency: form_CommerceBodyUpdate.currency,
        timezone: form_CommerceBodyUpdate.timezone,
        address: form_CommerceBodyUpdate.address,
        preparationTimeBuffer: form_CommerceBodyUpdate.preparationTimeBuffer,
        logoId: form_CommerceBodyUpdate.logoId,
      };
      try {
        const resCom = await handleUpdateCommerce(bodyCom);
        if (resCom) {
          setTimeout(() => {
            idToDelete.value = null;
            dialogStore.closeDialog();
            isLoaderLoginFormActivated.value = false;
          }, 1500);
        }
      } catch (error) {
        isGlobalError.value = true;
        setTimeout(() => {
          isGlobalError.value = false;
          isLoaderLoginFormActivated.value = false;
        }, 1000);
      }
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isLoaderLoginFormActivated.value = false;
      isGlobalError.value = false;
    }, 1000);
  }
};
const handleUplaodImageExist = async () => {
  const bodyCom: ICommerceBodyUpdate = {
    designation: form_CommerceBodyUpdate.designation,
    // orderCode: form_CommerceBodyUpdate.orderCode,
    // currency: form_CommerceBodyUpdate.currency,
    timezone: form_CommerceBodyUpdate.timezone,
    address: form_CommerceBodyUpdate.address,
    preparationTimeBuffer: form_CommerceBodyUpdate.preparationTimeBuffer,
    logoId: form_CommerceBodyUpdate.logoId,
  };
  try {
    isLoaderLoginFormActivated.value = true;
    const resCom = await handleUpdateCommerce(bodyCom);
    if (resCom) {
      setTimeout(() => {
        isAtcivatedWatch.value = !isAtcivatedWatch.value;
        isAtcivatedWatch.value = !isAtcivatedWatch.value;
        idToDelete.value = null;
        dialogStore.closeDialog();
        isLoaderLoginFormActivated.value = false;
      }, 1500);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
    }, 1000);
  }
};

const handleSubmit = async () => {
  if (!isImageUploadid.value) {
    globalHeaderTitleLayout.value =
      'Veuillez télécharger le logo du restaurant';
    isImageUploadid.value = true;
  } else {
    if (form_CommerceBodyUpdate.logoId) {
      handleUplaodImageExist();
    } else {
      handleUplaodImageIfExist();
    }
  }
};
</script>

<template>
  <form
    autocomplete="on"
    accept-charset="UTF-8"
    @submit.prevent="handleSubmit"
    class="space-y-6"
  >
    <div class="space-y-[16px]" v-if="!isImageUploadid">
      <TextInput
        required="true"
        v-model="form_CommerceBodyUpdate.designation"
        name="Entrez le nom du restaurant"
        placeholder="Entrez le nom du restaurant"
        type="text"
        id="Entrez le nom du restaurant"
        label="Entrez le nom du restaurant"
      />

      <!-- <TextInput
        required="true"
        v-model="form_CommerceBodyUpdate.orderCode"
        name="Entrez l'abréviation du restaurant"
        placeholder="Entrez l'abréviation du restaurant"
        type="text"
        id="Entrez l'abréviation du restaurant"
        label="Entrez l'abréviation du restaurant"
      /> -->
      <TextInput
        required="true"
        v-model="form_CommerceBodyUpdate.address"
        name="Entrez l'adresse  du restaurant"
        placeholder="Entrez l'adresse  du restaurant"
        type="text"
        id="Entrez l'adresse  du restaurant"
        label="Entrez l'adresse  du restaurant"
      />
      <TextInput
        required="false"
        v-model="form_CommerceBodyUpdate.preparationTimeBuffer"
        name="Entrez le temps de préparation en minutes par défaut"
        placeholder="Entrez le temps de préparation en minutes par défaut"
        type="text"
        id="Entrez le temps de préparation en minutes par défaut"
        label="Entrez le temps de préparation en minutes par défaut"
      />

      <!-- <div>
        <label for="currency" class="block mb-1 font-medium text-gray-700">
          Sélectionnez la devise du restaurant
        </label>

        <select
          v-model="form_CommerceBodyUpdate.currency"
          id="currency"
          name="currency"
          class="w-full px-3 py-2 border border-solid rounded-md focus:outline-none focus-within:border focus-within:border-solid border-border-input"
        >
          <option disabled value="">Sélectionnez une devise</option>
          <option value="EUR">Euro (EUR)</option>
          <option value="USD">Dollar US (USD)</option>
          <option value="GBP">Livre Sterling (GBP)</option>
        </select>
      </div> -->

      <div>
        <label for="timezone" class="mb-1 block font-medium text-gray-700">
          Entrez le fuseau horaire du restaurant
        </label>

        <select
          v-model="form_CommerceBodyUpdate.timezone"
          id="timezone"
          name="timezone"
          class="border-border-input w-full rounded-md border border-solid px-3 py-2 focus-within:border focus-within:border-solid focus:outline-none"
        >
          <option disabled value="">Sélectionnez un fuseau horaire</option>
          <option value="Europe/Brussels">Europe/Brussels (UTC+1)</option>

          <option value="America/New_York">America/New_York (UTC-5)</option>
          <option value="UTC">UTC</option>
        </select>
      </div>
    </div>

    <div
      class="mx-auto flex w-full max-w-sm flex-col items-center gap-4"
      v-else
    >
      <div
        v-if="!previewUrl"
        class="border-border-input flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed bg-white px-10 py-8 text-center transition hover:bg-gray-50"
        @click="triggerUpload"
      >
        <div
          v-html="IconUploadCloud"
          class="flex items-center justify-center"
        />

        <div class="space-y-1 text-sm text-gray-600">
          <p>Format : <span class="font-medium">PNG, JPEG, JPG</span></p>
          <p>Taille max : <span class="font-medium">5 Mo</span></p>
          <p>
            Résolution conseillée :
            <span class="font-medium">500 × 500 px</span>
          </p>
        </div>

        <span
          class="cursor-pointer text-sm text-indigo-600 underline hover:text-indigo-800"
        >
          Charger un fichier
        </span>

        <input
          ref="fileInput"
          type="file"
          accept="image/png, image/jpeg, image/jpg"
          class="hidden"
          @change="handleFileChange"
        />
      </div>

      <div
        v-else
        class="border-border-input relative h-48 w-full overflow-hidden rounded-2xl border border-dashed bg-white shadow-md"
      >
        <img
          :src="previewUrl"
          alt="Aperçu du logo"
          class="h-full w-full object-cover"
        />

        <!-- Bouton supprimer -->
        <button
          @click="handleDelete"
          class="absolute top-2 right-2 rounded bg-red-100 px-2 py-1 text-xs text-red-600 transition hover:bg-red-600 hover:text-white"
        >
          Supprimer
        </button>
      </div>
      <p v-if="errorMessage" class="text-center text-sm text-red-600">
        {{ errorMessage }}
      </p>
    </div>

    <div class="invisible h-[50px]" />

    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full">
          <BaseButtonPrimary
            :isClicked="isLoaderLoginFormActivated"
            type="submit"
            :label="isImageUploadid ? 'Modifier' : 'Suivant'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            :label="isImageUploadid ? 'Précèdent' : 'Annuler'"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </form>
</template>
